import type { PermissionEvent } from "../../../api/permissionHistoryApi";

/** Kind-wide grants this close together, by the same actor to the same person, are one act. */
const ONE_ACT_WITHIN_MS = 60_000;

/** One line of the permission history as the settings page shows it (accounts.md ST-3). */
export interface HistoryLine {
  /** The line's event, or the first of the kind-wide grants it groups. */
  readonly first: PermissionEvent;
  /** The kinds of the grouped kind-wide grants; `null` for a line of one event. */
  readonly itemKinds: readonly string[] | null;
}

/**
 * The history's events as lines, oldest first. The history keeps one event per curator
 * assignment (the fold and later revocations need each); consecutive kind-wide
 * `curation_granted` events for one person by one actor within a minute read as one line.
 * Old and new records alike: nothing is rewritten.
 */
export function historyLines(events: readonly PermissionEvent[]): HistoryLine[] {
  const lines: HistoryLine[] = [];
  for (const event of events) {
    const previous = lines.at(-1);
    if (previous !== undefined && event.itemKind !== null && joinsAct(previous.first, event)) {
      lines[lines.length - 1] = { first: previous.first, itemKinds: [...(previous.itemKinds ?? [previous.first.itemKind ?? ""]), event.itemKind] };
    } else {
      lines.push({ first: event, itemKinds: null });
    }
  }
  return lines;
}

function joinsAct(first: PermissionEvent, event: PermissionEvent): boolean {
  return (
    isKindWideGrant(event) &&
    isKindWideGrant(first) &&
    first.person?.userId === event.person?.userId &&
    first.actor?.userId === event.actor?.userId &&
    first.reconstructed === event.reconstructed &&
    Math.abs(Date.parse(event.at) - Date.parse(first.at)) <= ONE_ACT_WITHIN_MS
  );
}

function isKindWideGrant(event: PermissionEvent): boolean {
  return event.change === "curation_granted" && event.itemKind !== null && event.itemId === null && event.person !== null;
}
